import { useCallback, useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import {
  Alert,
  Avatar,
  Box,
  Button,
  Card,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  Stack,
  Tab,
  Tabs,
  TextField,
  Typography,
} from '@mui/material';
import PublishIcon from '@mui/icons-material/Publish';
import VideoLibraryIcon from '@mui/icons-material/VideoLibrary';
import PeopleIcon from '@mui/icons-material/People';
import api from '@/services/api';
import { useAuth } from '@/hooks/useAuth';
import AddGroupMembersDialog from '@/components/groups/AddGroupMembersDialog';
import GroupMemberRoleCard from '@/components/groups/GroupMemberRoleCard';
import GroupSessionCard from '@/components/groups/GroupSessionCard';
import PublishToGroupDialog from '@/components/groups/PublishToGroupDialog';
import ShareEditDialog from '@/components/review/ShareEditDialog';
import type { OfficialSourceOption } from '@/components/review/officialSource';
import PageHeader from '@/components/Layout/PageHeader';
import { usePageDocumentTitle } from '@/contexts/PageDocumentTitleContext';
import { buildOfficialSourceMemberOptions, filterVisibleGroupMembers } from '@/utils/groupMembers';
import {
  GROUP_ROLE_LABELS,
  type GroupSharedSession,
  type MainAppUser,
  type SessionShare,
  type ShareGroup,
} from '@/types';

function toShareRecord(session: GroupSharedSession, groupId: string): SessionShare {
  return {
    id: session.share_id,
    session_id: session.session_id,
    share_type: 'group',
    target_group_id: groupId,
    permission: session.permission as SessionShare['permission'],
    due_at: session.due_at,
    message: session.message,
    official_released_at: session.official_released_at,
    official_source_user_id: session.official_source_user_id,
    official_source_user_ids: session.official_source_user_ids,
    created_by_user_id: 0,
    created_at: session.shared_at,
  };
}

function memberLabel(m: GroupMember): string {
  return m.full_name?.trim() || m.reference_no;
}

interface GroupMember {
  user_id: number;
  reference_no: string;
  full_name?: string | null;
  photo_path?: string | null;
  photo_url?: string | null;
  role: string;
  group_role: string;
}

function groupInitial(name: string): string {
  return (name ?? '').trim().charAt(0).toUpperCase() || 'G';
}

export default function GroupDetailPage() {
  const { id } = useParams<{ id: string }>();
  const { user } = useAuth();
  const [group, setGroup] = useState<ShareGroup | null>(null);
  const [members, setMembers] = useState<GroupMember[]>([]);
  const [sessions, setSessions] = useState<GroupSharedSession[]>([]);
  const [tab, setTab] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [publishOpen, setPublishOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [editName, setEditName] = useState('');
  const [editDescription, setEditDescription] = useState('');
  const [addDialogRole, setAddDialogRole] = useState<'manager' | 'participant' | null>(null);
  const [editingShareSession, setEditingShareSession] = useState<GroupSharedSession | null>(null);

  usePageDocumentTitle(group?.name);

  const canManage = group?.can_manage ?? false;

  const visibleMembers = useMemo(() => filterVisibleGroupMembers(members), [members]);
  const managerMembers = useMemo(
    () => visibleMembers.filter((m) => m.group_role === 'manager'),
    [visibleMembers]
  );
  const participantMembers = useMemo(
    () => visibleMembers.filter((m) => m.group_role === 'participant'),
    [visibleMembers]
  );

  const officialSourceOptions = useMemo<OfficialSourceOption[]>(() => {
    const creatorId = editingShareSession?.session_created_by_user_id ?? group?.created_by_user_id;
    return buildOfficialSourceMemberOptions(members, creatorId, memberLabel, user?.id);
  }, [members, editingShareSession?.session_created_by_user_id, group?.created_by_user_id, user?.id]);

  const sessionCount = sessions.length;

  const load = useCallback(async () => {
    if (!id) return;
    setLoading(true);
    try {
      const [groupRes, sessionsRes] = await Promise.all([
        api.get<{ group: ShareGroup; members: GroupMember[] }>(`/groups/${id}`),
        api.get<{ sessions: GroupSharedSession[] }>(`/groups/${id}/sessions`),
      ]);
      setGroup(groupRes.data.group);
      setMembers(groupRes.data.members ?? []);
      setSessions(sessionsRes.data.sessions ?? []);
      setEditName(groupRes.data.group.name);
      setEditDescription(groupRes.data.group.description || '');
      setError(null);
    } catch {
      setError('無法載入群組');
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    load();
  }, [load]);

  const handleAddMembers = async (users: MainAppUser[], role: 'manager' | 'participant') => {
    if (!id || users.length === 0) return;
    await api.post(`/groups/${id}/members`, {
      user_ids: users.map((u) => u.id),
      group_role: role,
    });
    await load();
  };

  const handleRemoveMember = async (userId: number) => {
    if (!id || !confirm('確定移除此成員？')) return;
    await api.delete(`/groups/${id}/members/${userId}`);
    await load();
  };

  const handleRoleChange = async (userId: number, groupRole: 'manager' | 'participant') => {
    if (!id) return;
    await api.patch(`/groups/${id}/members/${userId}`, { group_role: groupRole });
    await load();
  };

  const handleSaveEdit = async () => {
    if (!id) return;
    await api.put(`/groups/${id}`, { name: editName, description: editDescription });
    setEditOpen(false);
    await load();
  };

  const handleToggleOfficialRelease = async (shareId: string, sessionId: string, published: boolean) => {
    await api.post(`/review-sessions/${sessionId}/release-official`, {
      share_id: shareId,
      group_id: id,
      published,
    });
    await load();
  };

  if (loading) return <Typography>載入中…</Typography>;
  if (error || !group) return <Alert severity="error">{error || '找不到群組'}</Alert>;

  return (
    <Box>
      <PageHeader
        backTo="/groups"
        backLabel="裁判群組"
        avatarContent={groupInitial(group.name)}
        icon={<PeopleIcon />}
        title={group.name}
        titleVariant="h5"
        subtitle={group.description || undefined}
        chips={
          <>
            <Chip size="small" icon={<PeopleIcon />} variant="outlined" label={`${visibleMembers.length} 位成員`} />
            <Chip size="small" icon={<VideoLibraryIcon />} variant="outlined" label={`${sessionCount} 部影片`} />
            {group.my_role && GROUP_ROLE_LABELS[group.my_role] && (
              <Chip size="small" label={GROUP_ROLE_LABELS[group.my_role]} color="secondary" />
            )}
          </>
        }
        actions={
          canManage ? (
            <>
              <Button size="small" variant="outlined" onClick={() => setEditOpen(true)}>
                編輯
              </Button>
              <Button size="small" variant="contained" color="secondary" startIcon={<PublishIcon />} onClick={() => setPublishOpen(true)}>
                發佈影片
              </Button>
            </>
          ) : undefined
        }
        mb={2.5}
      />

      <Tabs
        value={tab}
        onChange={(_, v) => setTab(v)}
        sx={{
          mb: 2,
          '& .MuiTab-root': { fontWeight: 700, minHeight: 48 },
        }}
      >
        <Tab icon={<VideoLibraryIcon sx={{ fontSize: 18 }} />} iconPosition="start" label={`分享影片 (${sessions.length})`} />
        <Tab icon={<PeopleIcon sx={{ fontSize: 18 }} />} iconPosition="start" label={`成員 (${visibleMembers.length})`} />
      </Tabs>

      {tab === 0 && (
        <Stack spacing={1.5}>
          {sessions.length === 0 ? (
            <Card variant="outlined" sx={{ borderRadius: 3, py: 5, textAlign: 'center', bgcolor: 'action.hover' }}>
              <VideoLibraryIcon sx={{ fontSize: 40, color: 'text.disabled', mb: 1 }} />
              <Typography fontWeight={700} gutterBottom>
                尚無分享影片
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {canManage ? '使用「發佈影片」將分析場次分享給群組成員' : '管理員發佈影片後會顯示於此'}
              </Typography>
            </Card>
          ) : (
            sessions.map((s) => (
              <GroupSessionCard
                key={s.share_id}
                session={s}
                groupId={id!}
                canManage={canManage}
                onToggleOfficialRelease={handleToggleOfficialRelease}
                onEditShare={setEditingShareSession}
              />
            ))
          )}
        </Stack>
      )}

      {tab === 1 && (
        <Grid container spacing={2}>
          <Grid item xs={12} md={6}>
            <GroupMemberRoleCard
              title="管理員"
              members={managerMembers}
              accent="info"
              canManage={canManage}
              ownerUserId={group.created_by_user_id}
              onAdd={() => setAddDialogRole('manager')}
              onRemove={handleRemoveMember}
              onRoleChange={handleRoleChange}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <GroupMemberRoleCard
              title="參與者"
              members={participantMembers}
              accent="secondary"
              canManage={canManage}
              ownerUserId={group.created_by_user_id}
              onAdd={() => setAddDialogRole('participant')}
              onRemove={handleRemoveMember}
              onRoleChange={handleRoleChange}
            />
          </Grid>
        </Grid>
      )}

      <AddGroupMembersDialog
        open={addDialogRole != null}
        role={addDialogRole || 'participant'}
        excludeIds={visibleMembers.map((m) => m.user_id)}
        onClose={() => setAddDialogRole(null)}
        onSubmit={(users) => handleAddMembers(users, addDialogRole || 'participant')}
      />

      <PublishToGroupDialog
        open={publishOpen}
        groupId={id!}
        groupName={group.name}
        onClose={() => setPublishOpen(false)}
        onPublished={load}
      />

      <ShareEditDialog
        open={!!editingShareSession}
        share={editingShareSession ? toShareRecord(editingShareSession, id!) : null}
        targetLabel={editingShareSession ? `${group.name} · ${editingShareSession.title}` : ''}
        canReleaseOfficial={
          (canManage || !!editingShareSession?.can_edit_share) &&
          editingShareSession?.permission === 'participant'
        }
        sessionOwnerUserId={editingShareSession?.session_created_by_user_id}
        officialSourceOptions={officialSourceOptions}
        onClose={() => setEditingShareSession(null)}
        onSaved={load}
      />

      <Dialog open={editOpen} onClose={() => setEditOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>編輯群組</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField label="群組名稱" value={editName} onChange={(e) => setEditName(e.target.value)} fullWidth />
            <TextField label="描述" value={editDescription} onChange={(e) => setEditDescription(e.target.value)} fullWidth multiline rows={3} />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditOpen(false)}>取消</Button>
          <Button variant="contained" color="secondary" onClick={handleSaveEdit}>
            儲存
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
